// ═══════════════════════════════════════════════════════════════════════════════
//  src/interview/PhoneticText.jsx
//  INTERVIEW ROOM — teleprompter
//
//  Exporta DOS vistas del mismo guion:
//
//    · PhoneticText  (por defecto) — la LISTA. Se usa antes de grabar: el
//      alumno ve la respuesta completa, frase a frase, y decide cuándo
//      empezar.
//
//    · ReadingBooth  (nombrada) — la CABINA. Se usa MIENTRAS graba.
//
//  ── POR QUÉ EXISTE LA CABINA ─────────────────────────────────────────────────
//  Con la lista completa en pantalla, el guion quedaba arriba y el cronómetro,
//  la transcripción y el botón de parar quedaban abajo. Leyendo en voz alta
//  había que subir para ver la frase y bajar para ver qué se estaba captando —
//  con el micrófono abierto y el reloj corriendo. Se rompía justo en el momento
//  que más concentración pide.
//
//  La cabina pone en UNA pantalla, sin scroll: la frase actual en grande, un
//  vistazo de la siguiente, lo que el micrófono está oyendo, el cronómetro y
//  el botón de parar. Es como funciona un teleprompter de verdad.
//
//  ── FONÉTICA ─────────────────────────────────────────────────────────────────
//  No se usa <ruby>: cada navegador le reserva un espacio distinto al anotador
//  y con interlineado alto se sale de la caja y pisa el renglón de arriba. La
//  guía es una burbuja posicionada sobre la palabra, en el hueco que el
//  interlineado reserva a propósito.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useRef } from 'react';

import { splitSentences, tokenize, plain } from './script';

/** Pinta una frase con sus palabras clave resaltadas. */
function Sentence({ frase }) {
  return tokenize(frase).map(t =>
    t.k === 'key' ? (
      <span key={t.i} className="iv-key">
        <span className="iv-key-ph" aria-hidden="true">{t.phonetic}</span>
        {t.word}
      </span>
    ) : (
      <span key={t.i}>{t.text}</span>
    )
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   LISTA — antes de grabar
   ═══════════════════════════════════════════════════════════════════════════ */
export default function PhoneticText({ text, at = 0, onAt }) {
  const frases = useMemo(() => splitSentences(text), [text]);
  if (!text) return null;

  return (
    <ol className="iv-script">
      {frases.map((frase, n) => {
        const estado = n < at ? 'iv-done' : n === at ? 'iv-now' : '';
        return (
          <li key={n} className={`iv-line ${estado}`}>
            <button
              type="button"
              className="iv-line-n"
              onClick={() => onAt?.(n === at ? n + 1 : n)}
              aria-label={n < at ? `Frase ${n + 1}, leída` : `Ir a la frase ${n + 1}`}
            >
              {n < at ? '✓' : n + 1}
            </button>
            <span className="iv-line-t"><Sentence frase={frase} /></span>
          </li>
        );
      })}
    </ol>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CABINA — mientras graba
   ═══════════════════════════════════════════════════════════════════════════ */
export function ReadingBooth({
  text, at = 0, onAt, elapsed = 0, transcript = '', onStop, phaseLabel,
}) {
  const frases = useMemo(() => splitSentences(text), [text]);
  const total = frases.length;
  const cur = Math.min(Math.max(at, 0), total - 1);
  const ultima = cur >= total - 1;

  const heardRef = useRef(null);
  // ¿Está la vista pegada al final? Empieza en true y sólo se suelta si el
  // alumno sube a releer algo.
  const pinned = useRef(true);

  /* ── La transcripción se queda pegada abajo ───────────────────────────────
     Antes esto usaba `scrollIntoView`, y por eso no funcionaba: ese método
     desplaza TODOS los contenedores con scroll de la cadena —incluida la
     página— en vez de mover sólo esta caja. El resultado era que la caja se
     quedaba arriba y había que bajar a mano cada vez que aparecía texto.

     Moviendo `scrollTop` directamente sólo se toca este contenedor. */
  useEffect(() => {
    const el = heardRef.current;
    if (el && pinned.current) el.scrollTop = el.scrollHeight;
  }, [transcript]);

  // Si el alumno sube a releer, se respeta: no se le arrastra al fondo con
  // cada palabra nueva. En cuanto vuelve al final, se vuelve a pegar solo.
  const onHeardScroll = () => {
    const el = heardRef.current;
    if (!el) return;
    pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  };

  const mm = Math.floor(elapsed / 60);
  const ss = String(elapsed % 60).padStart(2, '0');

  return (
    <section className="iv-booth" aria-label="Cabina de lectura">
      {/* ── Barra de estado ── */}
      <header className="iv-booth-top">
        <span className="iv-booth-pos">
          Frase <b className="iv-num">{cur + 1}</b> de <b className="iv-num">{total}</b>
        </span>
        {phaseLabel && <span className="iv-booth-phase">{phaseLabel}</span>}
        <span className="iv-spacer" />
        <span className="iv-dot" aria-hidden="true" />
        <span className="iv-booth-time iv-num">{mm}:{ss}</span>
        <span className="iv-wave" aria-hidden="true">
          {[0, 1, 2, 3, 4].map(b => <i key={b} />)}
        </span>
      </header>

      {/* ── Progreso de frases ── */}
      <div className="iv-booth-pips" aria-hidden="true">
        {frases.map((_, n) => (
          <i key={n} className={n < cur ? 'iv-done' : n === cur ? 'iv-now' : ''} />
        ))}
      </div>

      {/* ── La frase que toca ── */}
      <button
        type="button"
        className="iv-booth-line"
        onClick={() => !ultima && onAt?.(cur + 1)}
        aria-label="Avanzar a la siguiente frase"
      >
        <Sentence frase={frases[cur]} />
      </button>

      {/* ── Lo que viene ── */}
      {!ultima && (
        <p className="iv-booth-next">
          <span aria-hidden="true">↓</span> {plain(frases[cur + 1])}
        </p>
      )}

      {/* ── Lo que el micrófono está oyendo ── */}
      <div className="iv-booth-heard">
        <span className="iv-lbl">Te estoy oyendo</span>
        <div
          className="iv-booth-heard-t"
          ref={heardRef}
          onScroll={onHeardScroll}
          aria-live="polite"
        >
          {transcript || <em>…</em>}
        </div>
      </div>

      {/* ── Controles, siempre a la vista ── */}
      <div className="iv-booth-acts">
        {!ultima && (
          <button type="button" className="iv-btn" onClick={() => onAt?.(cur + 1)}>
            Siguiente frase →
          </button>
        )}
        <button
          type="button"
          className={`iv-btn iv-btn-rec ${ultima ? '' : 'iv-btn-narrow'}`}
          onClick={onStop}
        >
          ■ Terminé
        </button>
      </div>
    </section>
  );
}
